import React, { useEffect, useState } from "react";
import { Modal, Form } from "react-bootstrap";
import { Formik } from "formik";
import { useDispatch } from "react-redux";
import CustomSelect from "../../components/form/CustomSelect";
import InputField from "../../components/form/InputField";
import CommonButton from "../../components/commonButton";
import BannerDropzone from "../../components/BannerDropzone";
import { addProfileSuggestionCardSchema } from "../../schema/validationSchemas";
import withModal from "../../components/withModal";
import { fetchAvailableSuggestionKeys } from "../../redux/slice/profileSuggestionCardSlice";
import { FormLabel, FormFeedback } from "../../components/form/utils";

const keyLabelMap = {
    about_me: "About Me",
    create_post: "Create Post",
    add_experience: "Add Experience",
    add_education: "Add Education",
    add_product: "Add Product",
    add_services: "Add Services",
    add_certificate: "Add Certificate",
    add_marketplace_item: "Add Marketplace Item",
    add_properties: "Add Properties",
    create_business_page: "Create Business Page",
    purchase_subscription: "Purchase Subscription",
    add_verify_badge: "Add Verify Badge",
    profile_banner_images: "Profile Banner Images",
    sync_contact: "Sync Contact",
    complete_profile: "Complete Profile"
};

const AddEditProfileSuggestionCard = ({ onClose, onSubmit, item, isLoading, isView }) => {
    const dispatch = useDispatch();
    const [selectedFile, setSelectedFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [availableKeys, setAvailableKeys] = useState([]);
    const isEdit = !!item;

    const handleFileSelect = (e, file, setFieldTouched, values, setValues) => {
        e.stopPropagation();

        if (file) {
            setSelectedFile(file);
            setPreview(URL.createObjectURL(file));

            setValues({
                ...values,
                image: file,
                hasExistingImage: false,
            });
        } else {
            setSelectedFile(null);
            setPreview(null);

            setValues({
                ...values,
                image: null,
                hasExistingImage: false,
            });
        }

        setFieldTouched("image", true, false);
    };

    const initialValues = {
        key: item?.key || "",
        title: item?.title || "",
        description: item?.description || "",
        cta_text: item?.cta_text || "",
        cta_action: item?.cta_action || "",
        is_active: item?.is_active ?? true,
        image: null,
        hasExistingImage: Boolean(isEdit && item?.file?.url),
        isEdit: Boolean(isEdit),
    };

    const handleFormSubmit = async (values, { setSubmitting }) => {
        const formData = new FormData();
        formData.append("key", values.key);
        formData.append("title", values.title);
        formData.append("description", values.description);
        formData.append("cta_text", values.cta_text);
        formData.append("cta_action", values.cta_action);
        formData.append("is_active", values.is_active);

        if (selectedFile) {
            formData.append("file", selectedFile);
        }

        await onSubmit(formData);
        setSubmitting(false);
    };

    useEffect(() => {
        if (item?.file?.url) {
            setPreview(item.file.url);
            setSelectedFile(null);
        } else {
            setPreview(null);
            setSelectedFile(null);
        }
    }, [item]);

    useEffect(() => {
        if (!isView) {
            dispatch(fetchAvailableSuggestionKeys())
                .unwrap()
                .then((res) => {
                    if (res?.success && res?.data?.keys) {
                        setAvailableKeys(res.data.keys);
                    }
                })
                .catch((err) => {
                    console.error("Failed to fetch available keys:", err);
                });
        }
    }, [dispatch, isView]);

    const dropdownKeys = [...availableKeys];
    if (isEdit && item?.key && !dropdownKeys.includes(item.key)) {
        dropdownKeys.unshift(item.key);
    }

    const dropdownOptions = dropdownKeys.map((k) => ({
        value: k,
        label: keyLabelMap[k] || k,
    }));

    return (
        <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={addProfileSuggestionCardSchema}
            onSubmit={handleFormSubmit}
        >
            {({
                values,
                errors,
                touched,
                handleChange,
                setFieldValue,
                handleSubmit,
                isSubmitting,
                setFieldTouched,
                setValues,
            }) => (
                <Form onSubmit={handleSubmit} noValidate>
                    <Modal.Body>
                        <div className="grid gap-3 px-2 my-2">
                            {/* Key */}
                            <div className="g-col-md-6 mb-2">
                                {!isView ? (
                                    <CustomSelect
                                        label={{ name: "Key" }}
                                        name="key"
                                        placeholder="Select Suggestion Key"
                                        value={values.key}
                                        onChange={(opt) => setFieldValue("key", opt ? opt.value : "")}
                                        options={dropdownOptions}
                                        validation={{
                                            isInvalid: touched.key && errors.key,
                                            message: errors.key,
                                        }}
                                        required={true}
                                    />
                                ) : (
                                    <InputField
                                        label={{ name: "Key" }}
                                        required={true}
                                        input={{
                                            id: "key",
                                            name: "key",
                                            type: "text",
                                            value: values.key,
                                            disabled: true,
                                            className: "text-lowercase",
                                        }}
                                    />
                                )}
                            </div>

                            {/* Title */}
                            <InputField
                                className="g-col-md-6 mb-2"
                                label={{ name: "Title (Optional)" }}
                                input={{
                                    id: "title",
                                    name: "title",
                                    type: "text",
                                    placeholder: "Enter title",
                                    value: values.title,
                                    onChange: handleChange,
                                    disabled: isView,
                                }}
                                validation={{
                                    isInvalid: touched.title && errors.title,
                                    message: errors.title,
                                }}
                            />

                            {/* CTA Button Text */}
                            <InputField
                                className="g-col-md-6 mb-2"
                                label={{ name: "CTA Button Text (Optional)" }}
                                input={{
                                    id: "cta_text",
                                    name: "cta_text",
                                    type: "text",
                                    placeholder: "e.g. Add Experience",
                                    value: values.cta_text,
                                    onChange: handleChange,
                                    disabled: isView,
                                }}
                                validation={{
                                    isInvalid: touched.cta_text && errors.cta_text,
                                    message: errors.cta_text,
                                }}
                            />

                            {/* CTA Action */}
                            <InputField
                                className="g-col-md-6 mb-2"
                                label={{ name: "CTA Action / Route (Optional)" }}
                                input={{
                                    id: "cta_action",
                                    name: "cta_action",
                                    type: "text",
                                    placeholder: "e.g. experience",
                                    value: values.cta_action,
                                    onChange: handleChange,
                                    disabled: isView,
                                }}
                                validation={{
                                    isInvalid: touched.cta_action && errors.cta_action,
                                    message: errors.cta_action,
                                }}
                            />

                            {/* Status */}
                            {(isEdit || isView) && (
                                <div className="g-col-md-12 mb-2">
                                    <FormLabel labelName="Status" />
                                    <div className="d-flex gap-4 status-radio-group mt-2">
                                        <Form.Check
                                            type="radio"
                                            label="Active"
                                            name="is_active"
                                            id="active"
                                            checked={values.is_active === true}
                                            disabled={isView}
                                            onChange={() => !isView && setFieldValue("is_active", true)}
                                        />
                                        <Form.Check
                                            type="radio"
                                            label="Inactive"
                                            name="is_active"
                                            id="inactive"
                                            checked={values.is_active === false}
                                            disabled={isView}
                                            onChange={() => !isView && setFieldValue("is_active", false)}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Description */}
                            <InputField
                                className="g-col-md-12 mb-2"
                                label={{ name: "Description (Optional)" }}
                                input={{
                                    id: "description",
                                    name: "description",
                                    as: "textarea",
                                    rows: 3,
                                    placeholder: "Enter short description",
                                    value: values.description,
                                    onChange: handleChange,
                                    disabled: isView,
                                }}
                                validation={{
                                    isInvalid: touched.description && errors.description,
                                    message: errors.description,
                                }}
                            />

                            {/* Media Dropzone */}
                            <div className="g-col-md-12 mb-2">
                                <FormLabel labelName="Upload Media (Image or Video)" required={!isEdit} />
                                <BannerDropzone
                                    preview={preview}
                                    selectedFile={selectedFile}
                                    mimetype={item?.file?.mimetype}
                                    onFileSelect={(e, file) =>
                                        !isView &&
                                        handleFileSelect(
                                            e,
                                            file,
                                            setFieldTouched,
                                            values,
                                            setValues
                                        )
                                    }
                                    disabled={isView}
                                    existingImageMeta={{ name: item?.file?.file_name }}
                                />
                                {touched.image && errors.image && (
                                    <FormFeedback message={errors.image} />
                                )}
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer className="border-top-0 pt-0">
                        <CommonButton
                            type="button"
                            variant="outline"
                            title={isView ? "Close" : "Cancel"}
                            onClick={onClose}
                            disabled={isLoading || isSubmitting}
                        />
                        {!isView && (
                            <CommonButton
                                type="submit"
                                title={isEdit ? "Save Changes" : "Submit"}
                                isLoading={isLoading || isSubmitting}
                            />
                        )}
                    </Modal.Footer>
                </Form>
            )}
        </Formik>
    );
};

export default withModal(AddEditProfileSuggestionCard);
